/* ==========================================================================
   Happy Pet Mart — header behaviour
   Phase 3. Vanilla, no library, no dependency on the theme's jQuery 1.12.4.
   Loaded with `defer` from snippets/header_1.liquid.

   SCOPE — three jobs, deliberately small:
     1. Publish the real header height as --hpm-header-h, so every sticky
        offset in hpm-design.css derives from a measured value instead of a
        hardcoded guess.
     2. Mega-menu open/close for pointer and keyboard.
     3. Keep aria-expanded honest on the burger, whose open/close is still
        owned by the theme's own jQuery drawer.

   The mega-menu also opens on :hover and :focus-within in pure CSS, so it
   works with this file blocked or still loading. Everything here is
   progressive enhancement over that.
   ========================================================================== */

(function () {
  'use strict';

  var header = document.querySelector('[data-hpm-header]');
  if (!header) return;

  var root = document.documentElement;

  /* ---- 0. make the section wrapper the sticky element -------------------
     Shopify wraps each section in a div sized exactly to that section, and
     `position:sticky` cannot travel outside its parent box — so sticky on
     <header> alone scrolls away. Stamp the wrapper so hpm-design.css can
     stick that instead. The stylesheet also does this with :has(); this line
     covers browsers without it. */
  var sectionWrapper = header.closest('.shopify-section');
  if (sectionWrapper) sectionWrapper.classList.add('hpm-header-section');

  /* ---- 1. publish measured heights -------------------------------------
     The announcement bar is whatever sits above the header, so measure from
     the header's own offset rather than guessing which element it is.     */
  function publishHeights() {
    var bar = header.querySelector('.hpm-header__bar') || header;
    var h = Math.round(bar.getBoundingClientRect().height);
    if (h > 0) root.style.setProperty('--hpm-header-h', h + 'px');

    var top = Math.round(header.getBoundingClientRect().top + window.pageYOffset);
    root.style.setProperty('--hpm-announce-h', Math.max(0, top) + 'px');
  }

  publishHeights();

  if ('ResizeObserver' in window) {
    new ResizeObserver(publishHeights).observe(header);
  } else {
    window.addEventListener('resize', publishHeights, { passive: true });
  }

  /* ---- 2. mega-menu ----------------------------------------------------- */
  var toggles = [].slice.call(header.querySelectorAll('.hpm-nav__toggle'));

  function panelFor(toggle) {
    if (!toggle) return null;
    return document.getElementById(toggle.getAttribute('aria-controls'));
  }

  function setOpen(toggle, open) {
    if (!toggle) return;
    var panel = panelFor(toggle);
    if (!panel) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.toggleAttribute('hidden', !open);
    var item = toggle.closest('.hpm-nav__item');
    if (item) item.classList.toggle('is-open', open);
  }

  function closeAll(except) {
    toggles.forEach(function (t) {
      if (t !== except) setOpen(t, false);
    });
  }

  toggles.forEach(function (toggle) {
    /* Panels start `hidden` in the markup so they are out of the a11y tree
       before this runs. CSS hover/focus reveal needs them un-hidden, so hand
       visibility over to CSS now that JS is definitely present. */
    var panel = panelFor(toggle);
    if (panel) {
      panel.removeAttribute('hidden');
      panel.setAttribute('data-hpm-js', '');
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      var open = toggle.getAttribute('aria-expanded') === 'true';
      closeAll(toggle);
      setOpen(toggle, !open);
    });
  });

  /* Escape closes and returns focus to the toggle that owns the panel. */
  header.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var open = header.querySelector('.hpm-nav__item.is-open');
    if (!open) return;
    var toggle = open.querySelector('.hpm-nav__toggle');
    setOpen(toggle, false);
    if (toggle) toggle.focus();
  });

  /* A click anywhere outside the nav closes any panel opened by click. */
  document.addEventListener('click', function (e) {
    if (!header.contains(e.target)) closeAll(null);
  });

  /* Tabbing out of an item closes it, so keyboard users are not trailed by
     an open panel. Deferred a tick because focus lands on <body> in between. */
  header.addEventListener('focusout', function (e) {
    var item = e.target.closest('.hpm-nav__item');
    if (!item) return;
    window.setTimeout(function () {
      if (!item.contains(document.activeElement)) {
        var toggle = item.querySelector('.hpm-nav__toggle');
        if (toggle) setOpen(toggle, false);
      }
    }, 0);
  });

  /* ---- 3. burger aria state --------------------------------------------
     The drawer itself is opened by the theme's jQuery (theme.js
     headermobileAside), which toggles `.inside` on .mobile-off-canvas-active
     and knows nothing about aria. Mirror that class onto aria-expanded so the
     button reports its real state without taking ownership of the drawer. */
  var burger = header.querySelector('.hpm-header__burger');
  var drawer = document.querySelector('.mobile-off-canvas-active');

  if (burger && drawer && 'MutationObserver' in window) {
    new MutationObserver(function () {
      burger.setAttribute('aria-expanded', drawer.classList.contains('inside') ? 'true' : 'false');
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---- 4. image fade-in -------------------------------------------------
     Site-wide rather than header-only: this file is the one script rendered
     on every page, so it owns the opt-in for .hpm-img.

     Opt-in, not opt-out. hpm-design.css leaves images visible by default and
     only hides one once data-hpm-fade is set here — so a blocked or failed
     script can never strand an image at opacity 0, which is exactly what an
     earlier opt-out version did to all 58 product images.

     Images already decoded by the time this runs are skipped entirely: there
     is nothing to fade, and marking them would flash. `error` resolves the
     same as `load` so a broken image shows its alt text rather than nothing. */
  var faders = document.querySelectorAll('.hpm-img');
  Array.prototype.forEach.call(faders, function (img) {
    if (img.complete && img.naturalWidth > 0) return;

    img.setAttribute('data-hpm-fade', '');
    var reveal = function () { img.classList.add('is-loaded'); };
    img.addEventListener('load', reveal, { once: true });
    img.addEventListener('error', reveal, { once: true });
  });

  /* ---- 4b. scroll reveal ------------------------------------------------
     The single entrance animation (DESIGN-TOKENS §6.3): opacity 0 -> 1 with a
     16px rise, 380ms, one-shot.

     Opt-in for the same reason as the image fade. The hidden state is applied
     here and ONLY here, after confirming IntersectionObserver exists and the
     visitor has not asked for reduced motion — so there is no path where
     content is hidden with nothing able to reveal it. Elements are unobserved
     once shown, matching the design's one-shot behaviour. */
  var reveals = document.querySelectorAll('.hpm-reveal');

  if (reveals.length && 'IntersectionObserver' in window &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {

    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    Array.prototype.forEach.call(reveals, function (el) {
      el.setAttribute('data-hpm-reveal', '');
      revealObserver.observe(el);
    });
  }

  /* ---- 4c. shop-by-need tabs -------------------------------------------
     Panels are visible by default and only hidden once this runs, so with the
     script blocked all three read as stacked lists rather than one visible
     panel and two permanently gone. */
  Array.prototype.forEach.call(document.querySelectorAll('[data-hpm-tabs]'), function (tablist) {
    var section = tablist.closest('section');
    if (!section) return;

    var buttons = [].slice.call(tablist.querySelectorAll('.hpm-tabs__btn'));
    var panels = [].slice.call(section.querySelectorAll('[data-hpm-panel]'));
    if (buttons.length < 2 || panels.length < 2) return;

    function select(index) {
      buttons.forEach(function (b, i) {
        b.classList.toggle('is-active', i === index);
        b.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      panels.forEach(function (p, i) { p.classList.toggle('is-hidden', i !== index); });
    }

    select(0);
    buttons.forEach(function (b, i) {
      b.addEventListener('click', function () { select(i); });
      /* Left/Right arrows move between tabs, per the tablist pattern. */
      b.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var next = e.key === 'ArrowRight'
          ? (i + 1) % buttons.length
          : (i - 1 + buttons.length) % buttons.length;
        select(next);
        buttons[next].focus();
      });
    });
  });

  /* ---- 4d. the one pinned section --------------------------------------
     Three discrete steps chosen from scroll position — not a scrubbed scene.
     One rAF-throttled passive read, and the dimmed inactive state only applies
     after `is-ready` is added here, so without JS every step reads at full
     opacity. Reduced motion keeps the stepping but the stylesheet drops the
     movement. */
  var pin = document.querySelector('[data-hpm-pin]');
  if (pin) {
    var steps = [].slice.call(pin.querySelectorAll('[data-hpm-pin-step]'));
    var fills = [].slice.call(pin.querySelectorAll('[data-hpm-pin-fill]'));
    var counter = pin.querySelector('[data-hpm-pin-count]');

    if (steps.length) {
      pin.classList.add('is-ready');
      var current = -1;
      var ticking = false;

      var applyStep = function (index) {
        if (index === current) return;
        current = index;
        steps.forEach(function (s, i) { s.classList.toggle('is-active', i === index); });
        fills.forEach(function (f, i) { f.style.width = i <= index ? '100%' : '0%'; });
        if (counter) {
          counter.textContent =
            ('0' + (index + 1)).slice(-2) + ' / ' + ('0' + steps.length).slice(-2);
        }
      };

      var readPin = function () {
        var r = pin.getBoundingClientRect();
        var travel = r.height - window.innerHeight;
        var p = travel > 0 ? Math.min(1, Math.max(0, -r.top / travel)) : 0;
        applyStep(Math.min(steps.length - 1, Math.floor(p * steps.length)));
      };

      var onPinScroll = function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { ticking = false; readPin(); });
      };

      readPin();
      window.addEventListener('scroll', onPinScroll, { passive: true });
      window.addEventListener('resize', onPinScroll, { passive: true });
    }
  }

  /* ---- 4e. chew finder --------------------------------------------------
     Weight-based scoring: each option carries its own number, the chosen ones
     are summed, and the section's two thresholds pick the outcome. Index-based
     scoring would silently shift every result the moment someone added an
     option in the editor.

     FAIL-VISIBLE. Steps and outcomes are all rendered and all visible until
     `is-ready` is added here — with the script blocked the page reads as every
     question followed by every recommendation, which is verbose but leaves
     every product reachable. */
  var quiz = document.querySelector('[data-hpm-quiz]');
  if (quiz) {
    var qSteps = [].slice.call(quiz.querySelectorAll('[data-hpm-quiz-step]'));
    var qOutcomes = [].slice.call(quiz.querySelectorAll('[data-hpm-quiz-outcome]'));

    if (qSteps.length && qOutcomes.length) {
      var low = parseFloat(quiz.getAttribute('data-threshold-low'));
      var high = parseFloat(quiz.getAttribute('data-threshold-high'));
      if (isNaN(low)) low = 3;
      if (isNaN(high)) high = 6;

      var picks = [];
      quiz.classList.add('is-ready');

      var showStep = function (i) {
        qSteps.forEach(function (s, n) { s.classList.toggle('is-active', n === i); });
        qOutcomes.forEach(function (o) { o.classList.remove('is-active'); });
      };

      var showOutcome = function () {
        var total = picks.reduce(function (sum, p) { return sum + p.weight; }, 0);
        var key = total <= low ? 'light' : total <= high ? 'moderate' : 'heavy';

        var target = qOutcomes.filter(function (o) {
          return o.getAttribute('data-hpm-quiz-outcome') === key;
        })[0] || qOutcomes[0];

        qSteps.forEach(function (s) { s.classList.remove('is-active'); });
        qOutcomes.forEach(function (o) { o.classList.toggle('is-active', o === target); });

        /* Echo the answers back, so the recommendation is visibly a
           consequence of what was chosen rather than a black box. */
        var summary = target.querySelector('[data-hpm-quiz-summary]');
        var rows = target.querySelector('[data-hpm-quiz-summary-rows]');
        if (summary && rows) {
          rows.textContent = '';
          picks.forEach(function (p, n) {
            var row = document.createElement('div');
            row.className = 'hpm-finder__summary-row';
            var k = document.createElement('span');
            k.className = 'hpm-finder__summary-k';
            k.textContent = 'Question ' + (n + 1);
            var v = document.createElement('span');
            v.className = 'hpm-finder__summary-v';
            v.textContent = p.summary;
            row.appendChild(k);
            row.appendChild(v);
            rows.appendChild(row);
          });
          summary.hidden = picks.length === 0;
        }

        target.focus({ preventScroll: true });
      };

      quiz.addEventListener('click', function (e) {
        var pick = e.target.closest('[data-hpm-quiz-pick]');
        if (pick) {
          var stepEl = pick.closest('[data-hpm-quiz-step]');
          var idx = qSteps.indexOf(stepEl);
          picks = picks.slice(0, idx);
          picks.push({
            weight: parseFloat(pick.getAttribute('data-weight')) || 0,
            summary: pick.getAttribute('data-summary') || ''
          });
          if (idx + 1 < qSteps.length) showStep(idx + 1); else showOutcome();
          return;
        }

        if (e.target.closest('[data-hpm-quiz-back]')) {
          var back = e.target.closest('[data-hpm-quiz-step]');
          var bi = qSteps.indexOf(back);
          picks = picks.slice(0, Math.max(0, bi - 1));
          showStep(Math.max(0, bi - 1));
          return;
        }

        if (e.target.closest('[data-hpm-quiz-restart]')) {
          picks = [];
          showStep(0);
        }
      });

      qOutcomes.forEach(function (o) { o.setAttribute('tabindex', '-1'); });
      showStep(0);
    }
  }

  /* ---- 5. mobile filter sheet ------------------------------------------
     The design's mobile filters are a bottom sheet behind a "Filters" pill.
     The theme has the sidebar but no trigger, so the trigger and the scrim
     are injected here and hpm-design.css repositions the EXISTING sidebar
     into a sheet below 992px.

     Nothing is moved in the DOM. Relocating the sidebar would break the
     filter <form> that wraps it and the theme's own change handlers; a
     position change in CSS does the same job and cannot desynchronise. */
  var sidebar = document.querySelector('.shop-sidebar.storefront-filter');
  var toolbar = document.querySelector('.shop-top-bar');
  if (!sidebar || !toolbar) return;

  /* Tag the Bootstrap column so CSS can collapse it on mobile without the
     sheet leaving a hole in the page flow. */
  var sidebarCol = sidebar.closest('[class*="col-"]');
  if (sidebarCol) sidebarCol.classList.add('sidebar-wrapper-col');

  /* The theme ships every filter group collapsed — the group body carries an
     inline `display:none`, which no stylesheet can override. The design shows
     them open, and a filter panel whose options are all hidden behind
     accordions is a worse panel. Open them here and leave the theme's own
     slideToggle handler intact, so a shopper can still collapse a long group.
     The h5 also gets `widget-collapse-show`, which is what flips its glyph
     from + to −; the markup ships the class and the inline style disagreeing. */
  Array.prototype.forEach.call(sidebar.querySelectorAll('.widget-collapse-hide'), function (body) {
    body.style.display = '';
    var heading = body.parentNode && body.parentNode.querySelector('h5');
    if (heading) heading.classList.add('widget-collapse-show');
  });

  var activeCount = sidebar.querySelectorAll('.custom-control-input:checked').length;

  var trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'hpm-filter-trigger';
  trigger.setAttribute('aria-expanded', 'false');
  trigger.innerHTML =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M3.5 6.5h17M6.5 12h11M10 17.5h4"/></svg>Filters' +
    (activeCount ? '<span class="hpm-filter-trigger__count">' + activeCount + '</span>' : '');
  toolbar.insertBefore(trigger, toolbar.firstChild);

  var scrim = document.createElement('div');
  scrim.className = 'hpm-scrim';
  document.body.appendChild(scrim);

  function setSheet(open) {
    sidebar.classList.toggle('is-open', open);
    scrim.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.style.overflow = open ? 'hidden' : '';
  }

  trigger.addEventListener('click', function () {
    setSheet(!sidebar.classList.contains('is-open'));
  });

  scrim.addEventListener('click', function () { setSheet(false); });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sidebar.classList.contains('is-open')) {
      setSheet(false);
      trigger.focus();
    }
  });

  /* Crossing the breakpoint while the sheet is open would otherwise leave
     body overflow locked on a desktop layout that has no sheet. */
  window.addEventListener('resize', function () {
    if (window.innerWidth >= 992 && sidebar.classList.contains('is-open')) setSheet(false);
  }, { passive: true });
}());
